import type React from "react";
import { useEffect, useState } from "react";

import { Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useDeleteUserCourseTemplate } from "api/hooks/useDeleteUserCourseTemplate";
import { useGetUserCourseTemplates } from "api/hooks/useGetUserCourseTemplates";
import type { GetUserCourseTemplatesResponse } from "api/schemas/UserCourse";
import { useDeleteTreatmentSchedule, useGetTreatmentSchedules } from "api/services/treatment/hooks";
import type { TreatmentSchedulesItem } from "api/services/treatment/schemas/Treatment";
import type { Joint } from "api/types/GetActivityLibraryEntriesArgs";
import { useProfileContext } from "contexts/ProfileContext";
import { LibraryHeader } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryHeader";
import { LibraryItem } from "routes/patients/PatientProfile/components/UserCourse/components/LibraryItem";
import { SmallPrimaryButton } from "routes/patients/PatientProfile/components/UserCourse/components/SharedStyledComponents";
import {
  Header,
  JointImage,
  PopupRoot,
} from "routes/patients/PatientProfile/components/UserCourse/components/Templates";
import { useLibraryListSettings } from "routes/patients/PatientProfile/components/UserCourse/utils/useLibraryListSettings";
import { Avatar } from "shared/atoms/Avatar";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { LibraryItemSkeleton } from "shared/atoms/SkeletonLayout/components/LibraryItemSkeleton";
import { sortAlphabetically } from "utils/array/sort";
import { translatePainLocation, translateTemplateKey } from "utils/translations";

import { CardContainer, StyledPageContainer } from "../../components/styles";

import { filterTemplates, filterTreatmentSchedule, searchTemplate } from "./utils";

export const Templates: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  invariant(profile, "Profile should be defined");

  const { selectedSort, setSelectedSort, previewJoint, setPreviewJoint, searchTerm, setSearchTerm } =
    useLibraryListSettings("template_library_list_settings", true);

  const [jointOptions, setJointOptions] = useState<{ label: string; value: string }[]>([]);
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] = useState(false);

  const [filteredTreatmentSchedules, setFilteredTreatmentSchedules] = useState<TreatmentSchedulesItem[]>([]);
  const [selectedTreatmentSchedule, setSelectedTreatmentSchedule] = useState<null | number>(null);
  const { data: treatmentSchedulesData } = useGetTreatmentSchedules({
    limit: 1000,
  });
  const treatmentSchedules = treatmentSchedulesData?.items || [];
  const deleteTreatmentSchedule = useDeleteTreatmentSchedule();

  const [filteredTemplates, setFilteredTemplates] = useState<GetUserCourseTemplatesResponse>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<null | number>(null);
  const { data: templates, isError, isLoading, refetch: refetchTemplates } = useGetUserCourseTemplates();
  const deleteTemplate = useDeleteUserCourseTemplate({
    onSuccess: () => {
      refetchTemplates();
    },
  });

  // generate group options
  useEffect(() => {
    if (!templates?.length || !treatmentSchedules.length) {
      return;
    }

    const combinedItems = [...(treatmentSchedules || []), ...(templates || [])];

    const uniqueGroups = Array.from(new Set(combinedItems.filter(item => item.group).map(item => item.group)));
    const availableGroups = uniqueGroups
      .map(i => ({
        label: translatePainLocation({ painLocation: i as Joint }),
        value: i,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const mergedGroups = [
      { label: t("patients.pain_location.all"), value: "all" },
      { label: t("library.templates.custom"), value: "custom" },
      { label: t("library.templates.standardized"), value: "standardized" },
      ...availableGroups,
    ];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setJointOptions(mergedGroups);
  }, [templates, t]);

  // generate filtered templates
  useEffect(() => {
    if (!templates?.length) {
      setFilteredTemplates([]);
      return;
    }

    const templateWithNames = templates.map(template => {
      const name = template.key ? translateTemplateKey(template.key) : template.name || String(template.id);
      return { ...template, name };
    });
    const fTemplates = templateWithNames.filter(searchTemplate(searchTerm)).filter(filterTemplates(previewJoint));
    const sortedTemplates = sortAlphabetically({
      list: fTemplates,
      selectedSort,
      keySelector: template => template.name,
    });
    setFilteredTemplates(sortedTemplates);
  }, [templates, selectedSort, searchTerm, previewJoint]);

  // generate filtred treatment schedules
  useEffect(() => {
    if (!treatmentSchedules.length) {
      setFilteredTreatmentSchedules([]);
      return;
    }
    const treatmentSchedulesWithNames = treatmentSchedules.map(treatmentSchedule => {
      const name = treatmentSchedule.key
        ? translateTemplateKey(treatmentSchedule.key)
        : treatmentSchedule.name || String(treatmentSchedule.id);
      return { ...treatmentSchedule, name };
    });
    const fTreatmentSchedules = treatmentSchedulesWithNames
      .filter(searchTemplate(searchTerm))
      .filter(filterTreatmentSchedule(previewJoint));
    const sortedTreatmentSchedules = sortAlphabetically({
      list: fTreatmentSchedules,
      selectedSort,
      keySelector: treatmentSchedule => treatmentSchedule.name,
    });
    setFilteredTreatmentSchedules(sortedTreatmentSchedules);
  }, [treatmentSchedules, selectedSort, searchTerm, previewJoint]);

  const onDeleteTemplate = (templateId: number) => {
    deleteTemplate.mutateAsync({ therapistId: profile.id, templateId });
  };

  const onDeleteTreatmentSchedule = (treatmentScheduleId: number) => {
    deleteTreatmentSchedule.mutateAsync({ treatmentScheduleID: treatmentScheduleId });
  };

  const getSelectedName = () => {
    if (selectedTemplate !== null) {
      return templates?.find(tmp => tmp.id === selectedTemplate)?.name;
    }
    if (selectedTreatmentSchedule !== null) {
      return treatmentSchedules.find(ts => ts.id === selectedTreatmentSchedule)?.name;
    }
    return "";
  };

  const onClosePopup = () => {
    setShowDeleteConfirmationPopup(false);
    setSelectedTemplate(null);
    setSelectedTreatmentSchedule(null);
  };

  return (
    <StyledPageContainer isError={isError}>
      <StyledHeaderContainer>
        <LibraryHeader
          initialSelectedIds={[]}
          numberOfSelectedItems={0}
          markAll={() => undefined}
          unmarkAll={() => undefined}
          jointOptions={jointOptions}
          onSortChange={sort => {
            setSelectedSort(sort);
          }}
          onPreviewJointChange={joint => {
            setPreviewJoint(joint);
          }}
          onSearchChange={_search => {
            setSearchTerm(_search);
          }}
          onSelectedTypeOptionChange={() => undefined}
          libraryListSettingsStorageKey="template_library_list_settings"
        />
      </StyledHeaderContainer>

      <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">{t("library.treatment_templates_v2")}</Typography>
        <Typography variant="h5">
          {t("library.result_counter", { count: filteredTreatmentSchedules.length })}
        </Typography>
      </Stack>
      <CardContainer>
        {/* eslint-disable-next-line react/no-array-index-key */}
        {isLoading && [...Array(6)].map((_, index) => <LibraryItemSkeleton key={`template_v2_skeleton_${index}`} />)}

        {!isLoading &&
          filteredTreatmentSchedules?.map(treatmentSchedule => {
            const author =
              treatmentSchedule.creator_id !== null
                ? {
                    avatar: (
                      <Avatar
                        id={profile?.id}
                        size={40}
                        src={profile?.avatars?.medium || ""}
                        name={`${profile?.first_name} ${profile?.last_name}`}
                      />
                    ),
                    name: `${profile?.first_name} ${profile?.last_name}`,
                  }
                : undefined;

            const title = treatmentSchedule.key
              ? translateTemplateKey(treatmentSchedule.key)
              : treatmentSchedule.name || String(treatmentSchedule.id);

            return (
              <LibraryItem
                key={treatmentSchedule.id}
                author={author}
                checked={false}
                id={treatmentSchedule.id}
                image={treatmentSchedule.key ? <JointImage id={treatmentSchedule.key} /> : undefined}
                title={title}
                onClick={() => navigate(`${treatmentSchedule.id}/edit_template`)}
                onDelete={
                  treatmentSchedule.creator_id === null
                    ? undefined
                    : () => {
                        setSelectedTreatmentSchedule(treatmentSchedule.id);
                        setShowDeleteConfirmationPopup(true);
                      }
                }
              />
            );
          })}
      </CardContainer>

      <Divider sx={{ pt: 4 }} />

      <Stack flexDirection="row" justifyContent="space-between" alignItems="center" mb={3} mt={4}>
        <Typography variant="h5">{t("library.treatment_templates_v1")}</Typography>
        <Typography variant="h5">{t("library.result_counter", { count: filteredTemplates.length })}</Typography>
      </Stack>
      <CardContainer>
        {/* eslint-disable-next-line react/no-array-index-key */}
        {isLoading && [...Array(6)].map((_, index) => <LibraryItemSkeleton key={`template_v1_skeleton_${index}`} />)}

        {!isLoading &&
          filteredTemplates?.map(template => {
            const author = template.standardized
              ? undefined
              : {
                  avatar: (
                    <Avatar
                      id={profile?.id}
                      size={40}
                      src={profile?.avatars?.medium || ""}
                      name={`${profile?.first_name} ${profile?.last_name}`}
                    />
                  ),
                  name: `${profile?.first_name} ${profile?.last_name}`,
                };
            const title = template.key ? translateTemplateKey(template.key) : template.name || String(template.id);

            return (
              <LibraryItem
                key={template.id}
                author={author}
                checked={false}
                id={template.id}
                image={template.key ? <JointImage id={template.key} /> : undefined}
                title={title}
                onClick={() => navigate(`${template.id}`)}
                onDelete={
                  template.standardized
                    ? undefined
                    : () => {
                        setSelectedTemplate(template.id);
                        setShowDeleteConfirmationPopup(true);
                      }
                }
              />
            );
          })}
      </CardContainer>

      {showDeleteConfirmationPopup && (
        <MUIUnifiedModal onClose={onClosePopup} open showCloseButton={false} width="564px">
          <PopupRoot>
            <Header>{t("usercourse.templates.delete.cta")}</Header>
            <div>
              <div>
                {t("usercourse.templates.delete.body1", {
                  name: getSelectedName(),
                })}
              </div>
              <div>{t("usercourse.templates.delete.body2")}</div>
            </div>
            <div>
              <SmallPrimaryButton
                onClick={() => {
                  if (selectedTemplate !== null) {
                    onDeleteTemplate(selectedTemplate);
                  }
                  if (selectedTreatmentSchedule !== null) {
                    onDeleteTreatmentSchedule(selectedTreatmentSchedule);
                  }

                  onClosePopup();
                }}
              >
                {t("usercourse.templates.delete.cta")}
              </SmallPrimaryButton>
            </div>
          </PopupRoot>
        </MUIUnifiedModal>
      )}
    </StyledPageContainer>
  );
};

const StyledHeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
`;
